import { FC, useMemo } from 'react';

import { Heading } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { SingleSelectComboBox, SingleSelectComboBoxOption } from '@telialt/common-select-combo-box/src';
import { ProductRatePlan } from '@telialt/models/src/EshopModel';
import { selectPossibleRatePlans, selectRatePlanCode } from '@telialt/utils/src/eshopSelectors';
import { formatCurrency, isFloat } from '@telialt/utils/src/helpers';
import { useComponentId } from '@telialt/utils/src/hooks';

import { useAddRatePlanMutation } from '../../hooks/mutations/useAddRatePlanMutation';
import { useCartQuery } from '../../hooks/queries/useCartQuery';
import { useIsRatePlanMutating } from '../../hooks/useIsRatePlanMutating';
import { ESHOP_TEST_ID } from '../../test/test-ids';
import { RatePlanLegalText } from '../LegalText';
import { Loader } from '../Loader';
import {
    LeftColumn,
    RatePlanOptionContainer,
    RatePlanOptionDescription,
    RatePlanOptionOldPrice,
    RatePlanOptionPrice,
    RatePlanOptionTerms,
    RatePlanSelectorWrapper,
    RightColumn,
} from './RatePlanSelector.styles';

type RatePlanAsOption = ProductRatePlan & SingleSelectComboBoxOption;

const RatePlanOption: FC<ProductRatePlan> = (ratePlan) => {
    const { t } = useTranslations();
    const prices = ratePlan.prices[0];

    const oldPrice = isFloat(prices.oldPrice) ? formatCurrency(prices.oldPrice) : prices.oldPrice;

    const a11yLabels = useMemo(() => {
        const getPriceLabel = (price: string | number) => {
            return `${price} ${t['eshop.product_page.monthly_price_unit_accessible']}`;
        };

        return {
            oldPrice: `${t['eshop.product_page.old_price_accessible']} ${getPriceLabel(oldPrice)}`,
            price: `${t['eshop.product_page.price_accessible']} ${getPriceLabel(formatCurrency(prices.value))}`,
        };
    }, [oldPrice, prices.value, t]);

    return (
        <RatePlanOptionContainer>
            <LeftColumn>
                <RatePlanOptionDescription>{ratePlan.name}</RatePlanOptionDescription>
                <RatePlanOptionTerms {...ratePlan.planCharacteristics} />
            </LeftColumn>
            <RightColumn>
                {prices.oldPrice > 0 && (
                    <RatePlanOptionOldPrice aria-label={a11yLabels.oldPrice}>
                        <span aria-hidden={true}>
                            {oldPrice}
                            {` ${prices.currency}/${prices.period}`}
                        </span>
                    </RatePlanOptionOldPrice>
                )}
                <RatePlanOptionPrice $hasPromotion={prices.hasPromotion} aria-label={a11yLabels.price}>
                    <span aria-hidden={true}>{prices.formattedValue}</span>
                </RatePlanOptionPrice>
            </RightColumn>
        </RatePlanOptionContainer>
    );
};

export const RatePlanSelector = () => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const { addRatePlan } = useAddRatePlanMutation();
    const isLoaderActive = useIsRatePlanMutating();
    const id = useComponentId('rate-plan-selector');

    const ratePlans = selectPossibleRatePlans(cart);
    const ratePlanInCartCode = selectRatePlanCode(cart);

    const ratePlansAsOptions: RatePlanAsOption[] = ratePlans.map((ratePlan) => ({
        id: ratePlan.code,
        label: <RatePlanOption {...ratePlan} />,
        ariaLabel: undefined, // ToDo: when accessibility epic will be planned LTDAART-261, here yu can formulate hearable accessibility text
        ...ratePlan,
    }));

    const handleSelect = (ratePlan: RatePlanAsOption) => addRatePlan(ratePlan.code);

    return (
        <RatePlanSelectorWrapper data-test={ESHOP_TEST_ID.LEGACY_RATE_PLAN_SELECTOR}>
            <Loader isLoading={isLoaderActive} dataTest={ESHOP_TEST_ID.RATE_PLAN_LOADER} />
            <Heading tag="h3" variant="title-100" id={id}>
                {t['eshop.product_page.rate_plan_selection.title']}
            </Heading>
            <SingleSelectComboBox
                ariaLabelledBy={id}
                options={ratePlansAsOptions}
                initialOptionId={ratePlanInCartCode}
                onSelect={handleSelect}
                dataTest={ESHOP_TEST_ID.RATE_PLAN_SELECTOR}
            />
            <RatePlanLegalText />
        </RatePlanSelectorWrapper>
    );
};
